import { useState, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import "datatables.net-dt/js/dataTables.dataTables"
import "datatables.net-dt/css/jquery.dataTables.min.css"
import axios from "axios";
import { tokenDecode } from "../../utils/helper";
import ThemeMaker from "../../components/thememaker";
import { toast } from "react-toastify";
import { config } from "../../utils/config";
import { Paginated } from "../../components/Paginated";
import "../../components/table.css"
import "../../index.css"
import { Select, Modal as AntModal } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import CircularProgress from '@mui/material/CircularProgress';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import brandStyles from "../Brand/Brand.module.css";
import styles from "./ChannelMargin.module.css";
import "../../components/mcrbTableTheme.css";

function decodeHtml(str: any) {
    if (!str) return str;
    return String(str)
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#0?39;/g, "'");
}

function toDateInputValue(value: any) {
    if (!value) return "";
    return String(value).slice(0, 10);
}

function scopeTargetLabel(row: any) {
    if (row.scope_level === "category") return row.category_id || "-";
    if (row.scope_level === "brand") return [row.category_id, row.brand_id].filter(Boolean).join(" / ") || "-";
    if (row.scope_level === "item") return row.item_id || "-";
    return "-";
}

function scopePillClass(scope: any) {
    if (scope === "provider") return styles.pillViolet;
    if (scope === "category") return styles.pillBlue;
    if (scope === "brand") return styles.pillGreen;
    if (scope === "item") return styles.pillAmber;
    return styles.pillGrey;
}

// Format a margin value + type (percent -> "2.8%", amount -> "₹2.8", null -> "-")
function fmtMargin(value: any, type: any) {
    if (value === null || value === undefined || value === "") return "-";
    if (type === "amount") return `₹${value}`;
    if (type === "percent") return `${value}%`;
    return String(value);
}

const { baseNodeapiUrl, apiEndpoint } = config;

const toastOpts: any = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
};

const emptyRule = {
    rule_id: '',
    option_id: '',
    provider_id: '',
    scope_level: 'provider',
    category_id: '',
    brand_id: '',
    item_id: '',
    channel_margin_type: 'percent',
    channel_margin_value: '',
    title: 'Channel Margin',
    bap_id: '',
    domain: '',
    city: '',
    valid_from: '',
    valid_to: '',
};

const ChannelMargin = () => {
    const location = useLocation();
    const history = useHistory();
    const decode = tokenDecode();
    const { data } = decode;
    const id = data.user.seller_id;
    const isSuperAdmin = id === 1;
    const ownProviderId = data?.seller_data?.provider_id || "";
    const { permission: { channel_margin: channelMarginPermission } } = data;

    const [listing, setListing] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [listingLoading, setListingLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ruleList, setRuleList] = useState<any[]>([]);
    const [sellerList, setSellerList] = useState<any[]>([]);
    const [rule, setRule] = useState<any>({ ...emptyRule, provider_id: isSuperAdmin ? "" : ownProviderId });
    const [categoryList, setCategoryList] = useState<any[]>([]);
    const [itemList, setItemList] = useState<any[]>([]);
    const [itemListLoading, setItemListLoading] = useState(false);
    const [listFilters, setListFilters] = useState({ name: "", brand: "", category: "" });
    // Admin can narrow the product list to a single seller; a seller login is always locked to its own provider_id.
    const [listProviderId, setListProviderId] = useState("");

    // ---------------------------------------------------------------
    // Bulk grid state (redesigned "Create Channel Margin" experience)
    // Admin picks a scope, we list every target of that scope, and the
    // admin sets a margin type + value per row, then saves them in one
    // POST /admin/api/channel-margin call with a rules[] payload.
    // ---------------------------------------------------------------
    const SCOPE_TABS: { key: string; label: string }[] = isSuperAdmin
        ? [
            { key: "seller", label: "Seller wise" },
            { key: "category", label: "Category wise" },
            { key: "brand", label: "Brand wise" },
            { key: "item", label: "Item wise" },
        ]
        : [
            { key: "category", label: "Category wise" },
            { key: "brand", label: "Brand wise" },
            { key: "item", label: "Item wise" },
        ];

    const [bulkScope, setBulkScope] = useState<string>(isSuperAdmin ? "seller" : "category");
    const [bulkProviderId, setBulkProviderId] = useState<string>(isSuperAdmin ? "" : ownProviderId);
    const [bulkCategory, setBulkCategory] = useState<string>("");            // category name, used for brand scope
    const [itemCategoryFilter, setItemCategoryFilter] = useState<string>(""); // optional filter for item scope
    const [brandList, setBrandList] = useState<any[]>([]);
    const [brandListLoading, setBrandListLoading] = useState(false);
    const [gridSearch, setGridSearch] = useState("");
    const [gridRows, setGridRows] = useState<Record<string, { type: string; value: string }>>({});
    const [applyType, setApplyType] = useState("percent");
    const [applyValue, setApplyValue] = useState("");
    const [common, setCommon] = useState({ title: "", bap_id: "", domain: "", city: "", valid_from: "", valid_to: "" });
    const [savingBulk, setSavingBulk] = useState(false);

    const fetchBrandList = async () => {
        try {
            setBrandListLoading(true);
            const { data } = await axios({
                url: `${apiEndpoint}/getBrandList`,
                method: "POST",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                }
            });
            if (data.status === 200) setBrandList(data.data || []);
        } catch (error) {
        } finally {
            setBrandListLoading(false);
        }
    }

    const fetchSellerList = async () => {
        try {
            const { data } = await axios({
                url: `${apiEndpoint}/getsellerlist`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                }
            });
            if (data.status === 200) {
                setSellerList(data.data);
            }
        } catch (error) {
        }
    }

    const fetchCategoryList = async () => {
        try {
            const { data } = await axios({
                url: `${apiEndpoint}/getProtypeList`,
                method: "POST",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                }
            });
            if (data.status === 200) {
                setCategoryList(data.data);
            }
        } catch (error) {
        }
    }

    // Admin creating a rule for a seller must call get-product-list with the
    // seller's provider_id as a query param (per the real API contract); a
    // seller creating their own rule keeps calling it with their seller_id as before.
    const fetchItemList = async (sellerId: any) => {
        if (!sellerId) {
            setItemList([]);
            return;
        }
        try {
            setItemListLoading(true);
            const { data } = await axios({
                url: `${apiEndpoint}/get-product-list`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`
                },
                data: { seller_id: sellerId }
            });
            if (data.status === 200) {
                setItemList(data.data || []);
            } else {
                setItemList([]);
            }
        } catch (error) {
            setItemList([]);
        } finally {
            setItemListLoading(false);
        }
    }

    const fetchItemListByProvider = async (providerId: any) => {
        if (!providerId) {
            setItemList([]);
            return;
        }
        try {
            setItemListLoading(true);
            const { data } = await axios({
                url: `${apiEndpoint}/get-product-list`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                    "Accept": "application/json"
                },
                params: { provider_id: providerId }
            });
            if (data.status === 200) {
                setItemList(data.data || []);
            } else {
                setItemList([]);
            }
        } catch (error) {
            setItemList([]);
        } finally {
            setItemListLoading(false);
        }
    }

    useEffect(() => {
        if (rule.scope_level !== "item") return;
        if (isSuperAdmin) {
            fetchItemListByProvider(rule.provider_id);
        } else {
            const targetSellerId = (data?.user?.parent_id > 1 ? data.user.parent_id : data?.user?.seller_id);
            fetchItemList(targetSellerId);
        }
    }, [rule.scope_level, rule.provider_id]);

    useEffect(() => {
        if (isSuperAdmin) {
            fetchSellerList();
        }
        fetchCategoryList();
    }, []);

    // --- Bulk grid: load the target list for the active scope ---
    useEffect(() => {
        if (listing || isUpdate) return;
        if (bulkScope !== "item") return;
        if (isSuperAdmin) {
            fetchItemListByProvider(bulkProviderId);
        } else {
            const targetSellerId = (data?.user?.parent_id > 1 ? data.user.parent_id : data?.user?.seller_id);
            fetchItemList(targetSellerId);
        }
    }, [bulkScope, bulkProviderId, listing, isUpdate]);

    useEffect(() => {
        if (listing || isUpdate) return;
        if (bulkScope === "brand" && brandList.length === 0) fetchBrandList();
    }, [bulkScope, listing, isUpdate]);

    // Entered values must not leak across a different target set.
    useEffect(() => {
        setGridRows({});
        setGridSearch("");
    }, [bulkScope, bulkProviderId, bulkCategory, itemCategoryFilter]);

    const productCategoryName = (p: any) =>
        decodeHtml(p?.product_type_name || p?.category_name || p?.product_type || p?.category || p?.protype || "");

    const gridSource = useMemo(() => {
        if (bulkScope === "seller") {
            return sellerList.map((s: any) => ({
                id: String(s.provider_id),
                name: s.company_name || String(s.provider_id),
                sub: `Provider ${s.provider_id}`,
            }));
        }
        if (bulkScope === "category") {
            return categoryList
                .filter((c: any) => c.status)
                .map((c: any) => ({ id: c.name, name: decodeHtml(c.name), sub: "" }));
        }
        if (bulkScope === "brand") {
            return brandList
                .filter((b: any) => b.status)
                .map((b: any) => ({ id: String(b.id), name: b.brand_name, sub: "" }));
        }
        let items = itemList;
        if (itemCategoryFilter) {
            const f = itemCategoryFilter.toLowerCase();
            const filtered = items.filter((p: any) => productCategoryName(p).toLowerCase() === f);
            if (filtered.length) items = filtered;
        }
        return items.map((p: any) => ({
            id: String(p.ondc_product_id || p.id),
            name: decodeHtml(p.name),
            sub: p.sku ? `SKU ${p.sku}` : "",
        }));
    }, [bulkScope, sellerList, categoryList, brandList, itemList, itemCategoryFilter]);

    const filteredGrid = useMemo(() => {
        const q = gridSearch.trim().toLowerCase();
        if (!q) return gridSource;
        return gridSource.filter((r: any) =>
            String(r.name || "").toLowerCase().includes(q) || String(r.id || "").toLowerCase().includes(q)
        );
    }, [gridSource, gridSearch]);

    const filledCount = useMemo(
        () => Object.values(gridRows).filter((r) => r && r.value !== "" && r.value !== null).length,
        [gridRows]
    );

    const setRowField = (id: string, field: "type" | "value", val: string) => {
        setGridRows((prev) => {
            const row = prev[id] || { type: applyType, value: "" };
            return { ...prev, [id]: { ...row, [field]: val } };
        });
    }

    const applyToAll = () => {
        if (applyValue === "") {
            toast.error("Enter a value to apply !", toastOpts);
            return;
        }
        const next: Record<string, { type: string; value: string }> = {};
        filteredGrid.forEach((r: any) => {
            next[r.id] = { type: applyType, value: applyValue };
        });
        setGridRows(next);
    }

    const clearAll = () => setGridRows({});

    const commonChange = (e: any) => {
        const { name, value } = e.target;
        setCommon((prev) => ({ ...prev, [name]: value }));
    }

    const bulkSaveHandler = async () => {
        if (bulkScope !== "seller" && !bulkProviderId) {
            toast.error("Select a seller first !", toastOpts);
            return;
        }
        if (bulkScope === "brand" && !bulkCategory) {
            toast.error("Select a category first !", toastOpts);
            return;
        }
        const entries = Object.entries(gridRows).filter(
            ([, v]) => v && v.value !== "" && v.value !== null && !Number.isNaN(parseFloat(v.value))
        );
        if (entries.length === 0) {
            toast.error("Enter a margin value for at least one row !", toastOpts);
            return;
        }

        const rules = entries.map(([id, v]) => {
            const r: any = {
                channel_margin_type: v.type || "percent",
                channel_margin_value: parseFloat(v.value),
            };
            if (bulkScope === "seller") {
                r.scope_level = "provider";
                r.provider_id = id;
            } else if (bulkScope === "category") {
                r.scope_level = "category";
                r.provider_id = bulkProviderId;
                r.category_id = id;
            } else if (bulkScope === "brand") {
                r.scope_level = "brand";
                r.provider_id = bulkProviderId;
                r.category_id = bulkCategory;
                r.brand_id = id;
            } else {
                r.scope_level = "item";
                r.provider_id = bulkProviderId;
                r.item_id = id;
            }
            return r;
        });

        const payload: any = { rules };
        if (bulkScope !== "seller") payload.provider_id = bulkProviderId;
        if (common.title) payload.title = common.title;
        if (common.bap_id) payload.bap_id = common.bap_id;
        if (common.domain) payload.domain = common.domain;
        if (common.city) payload.city = common.city;
        if (common.valid_from) payload.valid_from = common.valid_from;
        if (common.valid_to) payload.valid_to = common.valid_to;

        try {
            setSavingBulk(true);
            const { data } = await axios({
                url: `${baseNodeapiUrl}/admin/api/channel-margin`,
                method: "POST",
                headers: { "Content-Type": "application/json" },
                data: payload,
            });
            if (data.status === 200) {
                toast.success(`${rules.length} Channel Margin rule(s) saved !`, toastOpts);
                goToList();
            } else {
                toast.error(data.message || "Failed to save rules !", toastOpts);
            }
        } catch (error) {
            toast.error("Something went wrong !", toastOpts);
        } finally {
            setSavingBulk(false);
        }
    }

    const setCreate = () => {
        setListing(false);
        setIsUpdate(false);
        setRule({ ...emptyRule, provider_id: isSuperAdmin ? "" : ownProviderId });
        setBulkScope(isSuperAdmin ? "seller" : "category");
        setBulkProviderId(isSuperAdmin ? "" : ownProviderId);
        setBulkCategory("");
        setItemCategoryFilter("");
        setGridRows({});
        setGridSearch("");
        setApplyValue("");
        setCommon({ title: "", bap_id: "", domain: "", city: "", valid_from: "", valid_to: "" });
    }

    const goToList = () => {
        history.push('/channelmarginlist');
        ruleListHandler();
    }

    const fieldChangeHandler = (e: any) => {
        const { name, value } = e.target;
        setRule((prev: any) => ({ ...prev, [name]: value }));
    }

    const itemChangeHandler = (value: any) => {
        setRule((prev: any) => ({ ...prev, item_id: value }));
    }

    const categoryChangeHandler = (value: any) => {
        setRule((prev: any) => ({ ...prev, category_id: value }));
    }

    // GET /admin/api/channel-margin/products — one row per seller product with its
    // resolved channel margin (buyer_app / seller_app / total_margin).
    // A seller login is locked to its own provider_id; an admin omits it for all
    // sellers, or passes one to narrow to a single seller.
    const ruleListHandler = async (
        filters = listFilters,
        providerId: string = isSuperAdmin ? listProviderId : ownProviderId,
    ) => {
        try {
            setListing(true);
            setListingLoading(true);
            const params = new URLSearchParams();
            if (providerId) params.append("provider_id", providerId);
            if (filters?.name?.trim()) params.append("name", filters.name.trim());
            if (filters?.brand?.trim()) params.append("brand", filters.brand.trim());
            if (filters?.category?.trim()) params.append("category", filters.category.trim());
            params.append("limit", "200");

            const { data } = await axios({
                url: `${baseNodeapiUrl}/admin/api/channel-margin/products?${params.toString()}`,
                method: "GET",
                headers: {
                    "x-custom-authorization": `Bearer ${sessionStorage.getItem('us_st_d')}`,
                },
            });

            if (data.status === 200) {
                setRuleList(Array.isArray(data.data) ? data.data : []);
            } else {
                toast.error(data.message || "Failed to fetch channel margins !", toastOpts);
            }
        } catch (error) {
            toast.error("Something went wrong !", toastOpts);
        } finally {
            setListingLoading(false);
        }
    }

    const editHandler = (item: any) => {
        setRule({
            rule_id: item.rule_id,
            option_id: item.option_id,
            provider_id: item.provider_id,
            scope_level: item.scope_level,
            category_id: item.category_id || "",
            brand_id: item.brand_id || "",
            item_id: item.item_id || "",
            channel_margin_type: item.channel_margin_type,
            channel_margin_value: item.channel_margin_value,
            title: item.title || "",
            bap_id: item.bap_id || "",
            domain: item.domain || "",
            city: item.city || "",
            valid_from: toDateInputValue(item.valid_from),
            valid_to: toDateInputValue(item.valid_to),
        });
        setIsUpdate(true);
        setListing(false);
    }

    const updateRuleHandler = async () => {
        try {
            setLoading(true);
            const payload: any = {
                rule_id: rule.rule_id,
                option_id: rule.option_id,
                channel_margin_type: rule.channel_margin_type,
                channel_margin_value: parseFloat(rule.channel_margin_value),
                title: rule.title,
            };
            if (rule.scope_level === "category") payload.category_id = rule.category_id;
            if (rule.scope_level === "brand") {
                payload.category_id = rule.category_id;
                payload.brand_id = rule.brand_id;
            }
            if (rule.scope_level === "item") payload.item_id = rule.item_id;
            if (rule.bap_id) payload.bap_id = rule.bap_id;
            if (rule.domain) payload.domain = rule.domain;
            if (rule.city) payload.city = rule.city;
            if (rule.valid_from) payload.valid_from = rule.valid_from;
            if (rule.valid_to) payload.valid_to = rule.valid_to;

            const { data } = await axios({
                url: `${baseNodeapiUrl}/admin/api/channel-margin`,
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                data: payload
            });

            if (data.status === 200) {
                toast.success("Channel Margin rule updated !", toastOpts);
                goToList();
            } else {
                toast.error(data.message || "Failed to update rule !", toastOpts);
            }
        } catch (error) {
            toast.error("Something went wrong !", toastOpts);
        } finally {
            setLoading(false);
        }
    }

    const deleteRule = async (item: any) => {
        try {
            const { data } = await axios({
                url: `${baseNodeapiUrl}/admin/api/channel-margin`,
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                data: { rule_id: item.rule_id, option_id: item.option_id }
            });

            if (data.status === 200) {
                setRuleList((prev) => prev.filter((r: any) => !(r.rule_id === item.rule_id && r.option_id === item.option_id)));
                toast.success("Channel Margin rule deleted !", toastOpts);
            } else {
                toast.error(data.message || "Failed to delete rule !", toastOpts);
            }
        } catch (error) {
            toast.error("Something went wrong !", toastOpts);
        }
    }

    const deleteRuleHandler = (item: any) => {
        AntModal.confirm({
            title: "Delete Channel Margin",
            icon: <ExclamationCircleFilled />,
            content: "Are you sure you want to delete this Channel Margin rule? This action cannot be undone.",
            okText: "Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: () => deleteRule(item),
        });
    }

    useEffect(() => {
        if (location.pathname === "/channelmarginlist") {
            if (channelMarginPermission?.read) ruleListHandler();
        } else {
            setCreate();
        }
      
    }, [location.pathname]);

    const COLUMNS = [
        {
            Header: "Sn.",
            Footer: "Sn.",
            accessor: (row: any, count: any) => {
                return <>{count + 1}</>;
            },
            disableFilters: true,
            sticky: "left",
        },
        {
            Header: "Product",
            Footer: "Product",
            accessor: (row: any) => (
                <div>
                    <div className={styles.gridNameCell}>{decodeHtml(row?.product_name) || "-"}</div>
                    {/* <div className={styles.gridSubtle}>ID {row?.product_id} · {row?.item_id}</div> */}
                </div>
            ),
            sticky: "left",
        },
        {
            Header: "Provider Id",
            Footer: "Provider Id",
            accessor: "provider_id",
            sticky: "left",
        },
        {
            Header: "Category",
            Footer: "Category",
            accessor: (row: any) => decodeHtml(row?.category) || "-",
            sticky: "left",
        },
        {
            Header: "Brand",
            Footer: "Brand",
            accessor: (row: any) => decodeHtml(row?.brand) || "-",
            sticky: "left",
        },
        {
            Header: "Buyer App Margin",
            Footer: "Buyer App Margin",
            accessor: (row: any) => (
                <span className={`${styles.pillBase} ${styles.pillBlue}`}>
                    {row?.buyer_app ? fmtMargin(row?.total_margin?.buyer_app_margin, row?.buyer_app?.channel_margin_type) : "-"}
                </span>
            ),
            sticky: "left",
        },
        {
            Header: "Seller App Margin",
            Footer: "Seller App Margin",
            accessor: (row: any) => (
                <span className={`${styles.pillBase} ${styles.pillGreen}`}>
                    {row?.seller_app ? fmtMargin(row?.total_margin?.seller_app_margin, row?.seller_app?.channel_margin_type) : "-"}
                </span>
            ),
            sticky: "left",
        },
        {
            Header: "Channel Margin Value",
            Footer: "Channel Margin Value",
            accessor: (row: any) => {
                const v = row?.total_margin?.combined_value;
                return v === null || v === undefined ? "-" : v;
            },
            sticky: "left",
        },
        {
            Header: "Channel Margin Type",
            Footer: "Channel Margin  Type",
            accessor: (row: any) => (
                <span className={`${styles.pillBase} ${styles.pillGrey}`}>
                    {row?.total_margin?.combined_type || "-"}
                </span>
            ),
            sticky: "left",
        },
    ].filter((col: any) =>
        // Buyer/Seller app margin breakdown is admin-only; a seller sees just the combined channel margin.
        isSuperAdmin || (col.Header !== "Buyer App Margin" && col.Header !== "Seller App Margin")
    );

    return (
        <ThemeMaker>
            <div className={`${styles.pageWrap} mcrb-page`}>
                <div className={`${brandStyles.cardOuter} mcrb-card`}>
                    <div className={brandStyles.headerBar}>
                        <span className={brandStyles.title}>
                            {listing ? "Channel Margin List" : (isUpdate ? "Update Channel Margin" : "Create Channel Margin")}
                        </span>
                        <div className="d-flex" style={{ gap: "10px" }}>
                            {/* {channelMarginPermission?.write ? (
                                <button className={brandStyles.primaryBtn} onClick={() => { history.push('/channelmargin'); setCreate(); }}>
                                    Create Channel Margin
                                </button>
                            ) : null} */}
                            {channelMarginPermission?.read ? (
                                <button className={brandStyles.primaryBtn} onClick={goToList}>
                                    Channel Margin List
                                </button>
                            ) : null}
                        </div>
                    </div>

                    {
                        listing ? (
                            <div className={`${brandStyles.bodyArea} ${brandStyles.tableWrap}`} style={{ minHeight: "60vh" }}>
                                {
                                    listingLoading ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
                                            <CircularProgress />
                                        </div>
                                    ) : (
                                        <>
                                            <div className={styles.bulkApplyBar}>
                                                {isSuperAdmin ? (
                                                    <div className={styles.bulkApplyField}>
                                                        <span className={styles.bulkApplyLabel}>Seller</span>
                                                        <select className={styles.miniSelect}
                                                            value={listProviderId}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                setListProviderId(v);
                                                                ruleListHandler(listFilters, v);
                                                            }}>
                                                            <option value="">All Sellers</option>
                                                            {sellerList.map((s: any, i: number) => (
                                                                <option key={i} value={s.provider_id}>{s?.company_name || s?.provider_id}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : null}
                                                <div className={styles.bulkApplyField}>
                                                    <span className={styles.bulkApplyLabel}>Name</span>
                                                    <input className={styles.miniInput} placeholder="Product name"
                                                        value={listFilters.name}
                                                        onChange={(e) => setListFilters((p) => ({ ...p, name: e.target.value }))}
                                                        onKeyDown={(e) => { if (e.key === "Enter") ruleListHandler(); }} />
                                                </div>
                                                <div className={styles.bulkApplyField}>
                                                    <span className={styles.bulkApplyLabel}>Brand</span>
                                                    <input className={styles.miniInput} placeholder="Brand"
                                                        value={listFilters.brand}
                                                        onChange={(e) => setListFilters((p) => ({ ...p, brand: e.target.value }))}
                                                        onKeyDown={(e) => { if (e.key === "Enter") ruleListHandler(); }} />
                                                </div>
                                                <div className={styles.bulkApplyField}>
                                                    <span className={styles.bulkApplyLabel}>Category</span>
                                                    <input className={styles.miniInput} placeholder="Category"
                                                        value={listFilters.category}
                                                        onChange={(e) => setListFilters((p) => ({ ...p, category: e.target.value }))}
                                                        onKeyDown={(e) => { if (e.key === "Enter") ruleListHandler(); }} />
                                                </div>
                                                <button type="button" className={styles.applyAllBtn} onClick={() => ruleListHandler()}>Apply</button>
                                                <button type="button" className={styles.clearAllBtn} onClick={() => {
                                                    const cleared = { name: "", brand: "", category: "" };
                                                    setListFilters(cleared);
                                                    setListProviderId("");
                                                    ruleListHandler(cleared, isSuperAdmin ? "" : ownProviderId);
                                                }}>Clear</button>
                                            </div>

                                            <div className={styles.desktopTable}>
                                                <Paginated data={ruleList} columns={COLUMNS} />
                                            </div>

                                            <div className={styles.mobileList}>
                                                {
                                                    ruleList.map((item: any, idx: number) => (
                                                        <div className={styles.mobileCard} key={`${item.product_id}-${item.item_id || idx}`}>
                                                            <div className={styles.mobileRow}>
                                                                <span className={styles.mobileLabel}>Product</span>
                                                                <span className={styles.mobileValue}>{decodeHtml(item?.product_name) || "-"}</span>
                                                            </div>
                                                            <div className={styles.mobileRow}>
                                                                <span className={styles.mobileLabel}>Provider</span>
                                                                <span className={styles.mobileValue}>{item?.provider_id}</span>
                                                            </div>
                                                            <div className={styles.mobileRow}>
                                                                <span className={styles.mobileLabel}>Category</span>
                                                                <span className={styles.mobileValue}>{decodeHtml(item?.category) || "-"}</span>
                                                            </div>
                                                            <div className={styles.mobileRow}>
                                                                <span className={styles.mobileLabel}>Brand</span>
                                                                <span className={styles.mobileValue}>{decodeHtml(item?.brand) || "-"}</span>
                                                            </div>
                                                            {isSuperAdmin ? (
                                                                <>
                                                                    <div className={styles.mobileRow}>
                                                                        <span className={styles.mobileLabel}>Buyer App Margin</span>
                                                                        <span className={`${styles.pillBase} ${styles.pillBlue}`}>{item?.buyer_app ? fmtMargin(item?.total_margin?.buyer_app_margin, item?.buyer_app?.channel_margin_type) : "-"}</span>
                                                                    </div>
                                                                    <div className={styles.mobileRow}>
                                                                        <span className={styles.mobileLabel}>Seller App Margin</span>
                                                                        <span className={`${styles.pillBase} ${styles.pillGreen}`}>{item?.seller_app ? fmtMargin(item?.total_margin?.seller_app_margin, item?.seller_app?.channel_margin_type) : "-"}</span>
                                                                    </div>
                                                                </>
                                                            ) : null}
                                                            <div className={styles.mobileRow}>
                                                                <span className={styles.mobileLabel}>Combined Value</span>
                                                                <span className={styles.mobileValue}>{item?.total_margin?.combined_value ?? "-"}</span>
                                                            </div>
                                                            <div className={styles.mobileRow}>
                                                                <span className={styles.mobileLabel}>Combined Type</span>
                                                                <span className={`${styles.pillBase} ${styles.pillGrey}`}>{item?.total_margin?.combined_type || "-"}</span>
                                                            </div>
                                                        </div>
                                                    ))
                                                }
                                            </div>

                                            {ruleList.length === 0 ? <p className={styles.emptyState}>No channel margins found !</p> : null}
                                        </>
                                    )
                                }
                            </div>
                        ) : isUpdate ? (
                                <div className={brandStyles.bodyArea} style={{ minHeight: "70vh" }}>
                                    <p className={styles.sectionTitle}>Scope &amp; Provider</p>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Provider<span className={brandStyles.required}>*</span></label>
                                                {
                                                    isSuperAdmin ? (
                                                        <select className={brandStyles.input} name="provider_id" disabled={isUpdate} value={rule.provider_id} onChange={fieldChangeHandler}>
                                                            <option value="">Select Seller</option>
                                                            {
                                                                sellerList.map((item: any, index: number) => (
                                                                    <option key={index} value={item.provider_id}>{item?.company_name}</option>
                                                                ))
                                                            }
                                                        </select>
                                                    ) : (
                                                        <input type="text" className={brandStyles.input} name="provider_id" value={rule.provider_id} disabled />
                                                    )
                                                }
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Scope Level<span className={brandStyles.required}>*</span></label>
                                                <select className={brandStyles.input} name="scope_level" disabled={isUpdate} value={rule.scope_level} onChange={fieldChangeHandler}>
                                                    <option value="provider">Provider (applies to all items)</option>
                                                    <option value="category">Category</option>
                                                    <option value="brand">Brand</option>
                                                    <option value="item">Item</option>
                                                </select>
                                            </div>
                                        </div>

                                        {
                                            rule.scope_level === "category" ? (
                                                <div className="col-md-6">
                                                    <div className={`${brandStyles.field} ${styles.field}`}>
                                                        <label className={brandStyles.label}>Category<span className={brandStyles.required}>*</span></label>
                                                        <Select
                                                            showSearch
                                                            allowClear
                                                            style={{ width: "100%" }}
                                                            disabled={isUpdate}
                                                            placeholder="Select Category"
                                                            value={rule.category_id || undefined}
                                                            onChange={categoryChangeHandler}
                                                            onClear={() => categoryChangeHandler("")}
                                                            optionFilterProp="label"
                                                            notFoundContent="No categories found"
                                                            options={categoryList
                                                                .filter((item: any) => item.status)
                                                                .map((item: any) => ({
                                                                    label: decodeHtml(item.name),
                                                                    value: item.name,
                                                                }))}
                                                        />
                                                    </div>
                                                </div>
                                            ) : null
                                        }

                                        {
                                            rule.scope_level === "item" ? (
                                                <div className="col-md-6">
                                                    <div className={`${brandStyles.field} ${styles.field}`}>
                                                        <label className={brandStyles.label}>Item<span className={brandStyles.required}>*</span></label>
                                                        <Select
                                                            showSearch
                                                            allowClear
                                                            style={{ width: "100%" }}
                                                            disabled={isUpdate}
                                                            loading={itemListLoading}
                                                            placeholder={!rule.provider_id ? "Select provider first" : "Select Item"}
                                                            value={rule.item_id || undefined}
                                                            onChange={itemChangeHandler}
                                                            onClear={() => itemChangeHandler("")}
                                                            optionFilterProp="label"
                                                            notFoundContent={itemListLoading ? "Loading..." : "No items found"}
                                                            options={itemList.map((item: any) => {
                                                                const label = `${decodeHtml(item.name)}${item.sku ? ` (${item.sku})` : ""}`;
                                                                return {
                                                                    label,
                                                                    value: item.ondc_product_id || item.id,
                                                                };
                                                            })}
                                                        />
                                                    </div>
                                                </div>
                                            ) : null
                                        }

                                        {
                                            rule.scope_level === "brand" ? (
                                                <>
                                                    <div className="col-md-6">
                                                        <div className={brandStyles.field}>
                                                            <label className={brandStyles.label}>Category</label>
                                                            <input type="text" className={brandStyles.input} value={rule.category_id} disabled />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className={brandStyles.field}>
                                                            <label className={brandStyles.label}>Brand</label>
                                                            <input type="text" className={brandStyles.input} value={rule.brand_id} disabled />
                                                        </div>
                                                    </div>
                                                </>
                                            ) : null
                                        }
                                    </div>

                                    <p className={styles.sectionTitle}>Margin Details</p>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Margin Type</label>
                                                <select className={brandStyles.input} name="channel_margin_type" value={rule.channel_margin_type} onChange={fieldChangeHandler}>
                                                    <option value="percent">Percent</option>
                                                    <option value="amount">Amount</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Margin Value<span className={brandStyles.required}>*</span></label>
                                                <input type="number" step="0.01" min="0" className={brandStyles.input} name="channel_margin_value"
                                                    placeholder={rule.channel_margin_type === "percent" ? "e.g. 0.50 for 0.50%" : "e.g. 10 for a fixed amount"}
                                                    value={rule.channel_margin_value} onChange={fieldChangeHandler} />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Title</label>
                                                <input type="text" className={brandStyles.input} name="title" placeholder="Channel Margin"
                                                    value={rule.title} onChange={fieldChangeHandler} />
                                            </div>
                                        </div>
                                    </div>

                                    <p className={styles.sectionTitle}>Optional Filters</p>
                                    <p className={styles.sectionHint}>Leave blank to apply this rule broadly.</p>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Buyer App (bap_id)</label>
                                                <input type="text" className={brandStyles.input} name="bap_id" value={rule.bap_id} onChange={fieldChangeHandler} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Domain</label>
                                                <input type="text" className={brandStyles.input} name="domain" value={rule.domain} onChange={fieldChangeHandler} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>City</label>
                                                <input type="text" className={brandStyles.input} name="city" value={rule.city} onChange={fieldChangeHandler} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Valid From</label>
                                                <input type="date" className={brandStyles.input} name="valid_from" value={rule.valid_from} onChange={fieldChangeHandler} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Valid To</label>
                                                <input type="date" className={brandStyles.input} name="valid_to" value={rule.valid_to} onChange={fieldChangeHandler} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.submitRow}>
                                        {
                                            channelMarginPermission?.update ? (
                                                <button type="submit" className={brandStyles.updateBtn} disabled={loading} onClick={updateRuleHandler}>
                                                    {loading ? "Updating..." : "Update"}
                                                </button>
                                            ) : null
                                        }
                                    </div>
                                </div>
                        ) : (
                                <div className={brandStyles.bodyArea} style={{ minHeight: "70vh" }}>
                                    <p className={styles.sectionTitle}>Choose what to set margins for</p>
                                    <div className={styles.scopeTabs}>
                                        {SCOPE_TABS.map((t) => (
                                            <button
                                                key={t.key}
                                                type="button"
                                                className={`${styles.scopeTab} ${bulkScope === t.key ? styles.scopeTabActive : ""}`}
                                                onClick={() => setBulkScope(t.key)}
                                            >
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>

                                    <div className={`row ${styles.contextRow}`}>
                                        {
                                            bulkScope !== "seller" && isSuperAdmin ? (
                                                <div className="col-md-4">
                                                    <div className={brandStyles.field}>
                                                        <label className={brandStyles.label}>Seller<span className={brandStyles.required}>*</span></label>
                                                        <select className={brandStyles.input} value={bulkProviderId} onChange={(e) => setBulkProviderId(e.target.value)}>
                                                            <option value="">Select Seller</option>
                                                            {sellerList.map((s: any, i: number) => (
                                                                <option key={i} value={s.provider_id}>{s?.company_name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            ) : null
                                        }

                                        {
                                            bulkScope === "brand" ? (
                                                <div className="col-md-4">
                                                    <div className={`${brandStyles.field} ${styles.field}`}>
                                                        <label className={brandStyles.label}>Category<span className={brandStyles.required}>*</span></label>
                                                        <Select
                                                            showSearch
                                                            allowClear
                                                            style={{ width: "100%" }}
                                                            placeholder="Select Category"
                                                            value={bulkCategory || undefined}
                                                            onChange={(v: any) => setBulkCategory(v || "")}
                                                            optionFilterProp="label"
                                                            notFoundContent="No categories found"
                                                            options={categoryList
                                                                .filter((c: any) => c.status)
                                                                .map((c: any) => ({ label: decodeHtml(c.name), value: c.name }))}
                                                        />
                                                    </div>
                                                </div>
                                            ) : null
                                        }

                                        {
                                            bulkScope === "item" ? (
                                                <div className="col-md-4">
                                                    <div className={`${brandStyles.field} ${styles.field}`}>
                                                        <label className={brandStyles.label}>Filter by Category</label>
                                                        <Select
                                                            showSearch
                                                            allowClear
                                                            style={{ width: "100%" }}
                                                            placeholder="All categories"
                                                            value={itemCategoryFilter || undefined}
                                                            onChange={(v: any) => setItemCategoryFilter(v || "")}
                                                            optionFilterProp="label"
                                                            notFoundContent="No categories found"
                                                            options={categoryList
                                                                .filter((c: any) => c.status)
                                                                .map((c: any) => ({ label: decodeHtml(c.name), value: c.name }))}
                                                        />
                                                    </div>
                                                </div>
                                            ) : null
                                        }
                                    </div>

                                    <div className={styles.bulkApplyBar}>
                                        <div className={styles.bulkApplyField}>
                                            <span className={styles.bulkApplyLabel}>Margin Type</span>
                                            <select className={styles.miniSelect} value={applyType} onChange={(e) => setApplyType(e.target.value)}>
                                                <option value="percent">Percent</option>
                                                <option value="amount">Amount</option>
                                            </select>
                                        </div>
                                        <div className={styles.bulkApplyField}>
                                            <span className={styles.bulkApplyLabel}>Margin Value</span>
                                            <input type="number" step="0.01" min="0" className={styles.miniInput}
                                                placeholder={applyType === "percent" ? "e.g. 0.5" : "e.g. 10"}
                                                value={applyValue} onChange={(e) => setApplyValue(e.target.value)} />
                                        </div>
                                        <button type="button" className={styles.applyAllBtn} onClick={applyToAll}>Apply to listed rows</button>
                                        <button type="button" className={styles.clearAllBtn} onClick={clearAll}>Clear all</button>
                                    </div>

                                    <div className={styles.gridToolbar}>
                                        <input className={styles.gridSearch} placeholder="Search…"
                                            value={gridSearch} onChange={(e) => setGridSearch(e.target.value)} />
                                        <span className={styles.rowCountHint}><b>{filledCount}</b> of {gridSource.length} will be saved</span>
                                    </div>

                                    <div className={styles.gridScroll}>
                                        <table className={styles.marginGrid}>
                                            <thead>
                                                <tr>
                                                    <th>{bulkScope === "seller" ? "Seller" : bulkScope === "category" ? "Category" : bulkScope === "brand" ? "Brand" : "Item"}</th>
                                                    <th style={{ width: 170 }}>Margin Type</th>
                                                    <th style={{ width: 170 }}>Margin Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    ((bulkScope === "item" && itemListLoading) || (bulkScope === "brand" && brandListLoading)) ? (
                                                        <tr><td colSpan={3} className={styles.gridEmpty}>Loading…</td></tr>
                                                    ) : filteredGrid.length === 0 ? (
                                                        <tr><td colSpan={3} className={styles.gridEmpty}>
                                                            {bulkScope !== "seller" && !bulkProviderId ? "Select a seller to load the list" : "Nothing to show"}
                                                        </td></tr>
                                                    ) : (
                                                        filteredGrid.map((r: any) => {
                                                            const row = gridRows[r.id];
                                                            return (
                                                                <tr key={r.id} className={row && row.value !== "" ? styles.rowActive : ""}>
                                                                    <td>
                                                                        <div className={styles.gridNameCell}>{r.name}</div>
                                                                        {r.sub ? <div className={styles.gridSubtle}>{r.sub}</div> : null}
                                                                    </td>
                                                                    <td>
                                                                        <select className={styles.miniSelect}
                                                                            value={row?.type || applyType}
                                                                            onChange={(e) => setRowField(r.id, "type", e.target.value)}>
                                                                            <option value="percent">Percent</option>
                                                                            <option value="amount">Amount</option>
                                                                        </select>
                                                                    </td>
                                                                    <td>
                                                                        <input type="number" step="0.01" min="0" className={styles.miniInput}
                                                                            placeholder="—"
                                                                            value={row?.value ?? ""}
                                                                            onChange={(e) => setRowField(r.id, "value", e.target.value)} />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )
                                                }
                                            </tbody>
                                        </table>
                                    </div>

                                    <p className={styles.sectionTitle}>Optional Filters</p>
                                    <p className={styles.sectionHint}>Applied to every rule in this batch. Leave blank to apply broadly.</p>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Title</label>
                                                <input type="text" className={brandStyles.input} name="title" placeholder="Channel Margin"
                                                    value={common.title} onChange={commonChange} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Buyer App (bap_id)</label>
                                                <input type="text" className={brandStyles.input} name="bap_id" value={common.bap_id} onChange={commonChange} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Domain</label>
                                                <input type="text" className={brandStyles.input} name="domain" value={common.domain} onChange={commonChange} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>City</label>
                                                <input type="text" className={brandStyles.input} name="city" value={common.city} onChange={commonChange} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Valid From</label>
                                                <input type="date" className={brandStyles.input} name="valid_from" value={common.valid_from} onChange={commonChange} />
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className={brandStyles.field}>
                                                <label className={brandStyles.label}>Valid To</label>
                                                <input type="date" className={brandStyles.input} name="valid_to" value={common.valid_to} onChange={commonChange} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.submitRow}>
                                        {
                                            channelMarginPermission?.write ? (
                                                <button type="button" className={brandStyles.saveBtn} disabled={savingBulk} onClick={bulkSaveHandler}>
                                                    {savingBulk ? "Saving…" : `Save${filledCount ? ` (${filledCount})` : ""}`}
                                                </button>
                                            ) : null
                                        }
                                    </div>
                                </div>
                            )
                        }
                    </div>
                </div>
        </ThemeMaker>
    )
}

export default ChannelMargin;
